import isEqual from "lodash/isEqual";

import { RuleValueFormat, ValueSource } from "constants/ruleBooks";

import {
  CheckComparator,
  FieldType,
  FieldValueTypeRuleValueFormatMap,
  FieldValueTypesByFieldType,
} from "./constants";

export const PropertyType = {
  INTEGER: 1,
  WEIGHT: 2,
  CURRENCY: 4,
  BOOLEAN: 8,
  BUSINESS: 16,
  BUSINESS_NAME: 32,
  TEXT: 64,
  LIST: 128,
  TAX_TYPE: 256,
  GL_CODE: 512,
  REAL: 1024,
};

export const PropertyTypeValueMap = {
  boolean: PropertyType.BOOLEAN,
  business: PropertyType.BUSINESS,
  currency: PropertyType.CURRENCY,
  currency_cents: PropertyType.CURRENCY,
  gl_code: PropertyType.TEXT,
  integer: PropertyType.INTEGER,
  list: PropertyType.LIST,
  tax_type: PropertyType.TEXT,
  text: PropertyType.TEXT,
  percentage: PropertyType.REAL,
  weight: PropertyType.WEIGHT,
  weight_grams: PropertyType.WEIGHT,
};

export const PropertyTypeInputTypeMap = {
  [PropertyType.BOOLEAN]: "checkbox",
  [PropertyType.BUSINESS]: "text",
  [PropertyType.CURRENCY]: "number",
  [PropertyType.INTEGER]: "number",
  [PropertyType.REAL]: "number",
  [PropertyType.TEXT]: "text",
  [PropertyType.WEIGHT]: "number",
};

const CURRENCY_PROPS = {
  type: PropertyTypeInputTypeMap[PropertyType.CURRENCY],
  step: 0.0001,
};

const WEIGHT_PROPS = {
  type: PropertyTypeInputTypeMap[PropertyType.WEIGHT],
  step: 1,
};

const BOOLEAN_PROPS = {
  type: PropertyTypeInputTypeMap[PropertyType.BOOLEAN],
};

const INTEGER_PROPS = {
  type: PropertyTypeInputTypeMap[PropertyType.INTEGER],
  step: 1,
};

const REAL_PROPS = {
  type: PropertyTypeInputTypeMap[PropertyType.REAL],
};

const TEXT_PROPS = {
  type: PropertyTypeInputTypeMap[PropertyType.TEXT],
};

const BUSINESS_PROPS = {
  type: PropertyTypeInputTypeMap[PropertyType.BUSINESS],
};

export const PropertyTypeInputPropsMap = {
  [PropertyType.BUSINESS]: BUSINESS_PROPS,
  [PropertyType.BOOLEAN]: BOOLEAN_PROPS,
  [PropertyType.CURRENCY]: CURRENCY_PROPS,
  [PropertyType.INTEGER]: INTEGER_PROPS,
  [PropertyType.REAL]: REAL_PROPS,
  [PropertyType.TEXT]: TEXT_PROPS,
  [PropertyType.WEIGHT]: WEIGHT_PROPS,
};

export function getPropertyType(propertyOrRelation) {
  if (
    typeof propertyOrRelation === "object" &&
    typeof propertyOrRelation.valueType === "string"
  ) {
    return (
      PropertyTypeValueMap[propertyOrRelation.valueType] || PropertyType.TEXT
    );
  }
  return PropertyType.TEXT;
}

export function getInputPropsForPropertyType(propertyType) {
  return PropertyTypeInputPropsMap[propertyType] || TEXT_PROPS;
}

const NUMBER_COMPARATORS = [
  CheckComparator.EQUALS,
  CheckComparator.NOT_EQUAL,
  CheckComparator.LESS_THAN,
  CheckComparator.GREATER_THAN,
  CheckComparator.LESS_THAN_OR_EQUAL,
  CheckComparator.GREATER_THAN_OR_EQUAL,
];

const TEXT_COMPARATORS = [
  CheckComparator.EQUALS,
  CheckComparator.NOT_EQUAL,
  CheckComparator.CONTAINS,
  CheckComparator.NOT_CONTAINS,
  CheckComparator.STARTS_WITH,
  CheckComparator.ENDS_WITH,
];

const LIST_COMPARATORS = [
  CheckComparator.CONTAINS,
  CheckComparator.NOT_CONTAINS,
];

const BOOLEAN_COMPARATORS = [CheckComparator.EQUALS, CheckComparator.NOT_EQUAL];

const BUSINESS_COMPARATORS = [
  CheckComparator.EQUALS,
  CheckComparator.NOT_EQUAL,
];

export const PropertyTypeCheckComparatorMap = {
  [PropertyType.BOOLEAN]: BOOLEAN_COMPARATORS,
  [PropertyType.BUSINESS]: BUSINESS_COMPARATORS,
  [PropertyType.CURRENCY]: NUMBER_COMPARATORS,
  [PropertyType.INTEGER]: NUMBER_COMPARATORS,
  [PropertyType.LIST]: LIST_COMPARATORS,
  [PropertyType.REAL]: NUMBER_COMPARATORS,
  [PropertyType.TEXT]: TEXT_COMPARATORS,
  [PropertyType.WEIGHT]: NUMBER_COMPARATORS,
};

export const RoundingMethod = {
  CEILING: "CEILING",
  DOWN: "DOWN",
  FLOOR: "FLOOR",
  HALF_EVEN: "HALF_EVEN",
  HALF_DOWN: "HALF_DOWN",
  HALF_UP: "HALF_UP",
  LAST_05_UP: "LAST_05_UP",
  NONE: "NONE",
  UP: "UP",
};

export function getValueFormatByFieldType(
  fieldType,
  defaultRuleValueFormat = RuleValueFormat.UNITS,
) {
  return FieldValueTypeRuleValueFormatMap[fieldType] || defaultRuleValueFormat;
}

export const RoundingMethodOptions = [
  { value: RoundingMethod.NONE, label: "None" },
  { value: RoundingMethod.UP, label: "Up" },
  { value: RoundingMethod.DOWN, label: "Down" },
  { value: RoundingMethod.HALF_UP, label: "Half Up" },
  { value: RoundingMethod.HALF_DOWN, label: "Half Down" },
  { value: RoundingMethod.HALF_EVEN, label: "Half Even" },
  { value: RoundingMethod.CEILING, label: "Ceiling" },
  { value: RoundingMethod.FLOOR, label: "Floor" },
  { value: RoundingMethod.LAST_05_UP, label: "Last 5 Up" },
];

export function compareDynamicFieldOption(field) {
  return option => isEqual(option.value, field.value);
}

export function mapOptionToConstantFieldOption(option) {
  return {
    label: option.label,
    value: {
      source: ValueSource.CONSTANT,
      value: option.value,
    },
  };
}

export function parseBooleanValue(fieldValue) {
  // the source of the data could be an `<option>` value, which returns as a string
  // or an `input type="checkbox"`, which returns a boolean.
  if (fieldValue === "true" || fieldValue === true || fieldValue === 1) {
    return true;
  }
  if (fieldValue === "false" || fieldValue === false || fieldValue === 0) {
    return false;
  }
  // eslint-disable-next-line no-console
  console.warn("Unexpected Boolean value %s", fieldValue);
  // this is not expected
  return fieldValue;
}

export function parseInternalIndex(internalIndex) {
  if (typeof internalIndex === "string") {
    // if the index is a string, parse it to an integer
    return parseInt(internalIndex, 10);
  }
  if (typeof internalIndex === "number") {
    return internalIndex;
  }
  return null;
}

export function parseCheckValue(fieldType, fieldValue) {
  if (FieldValueTypesByFieldType[FieldType.BOOLEAN].includes(fieldType)) {
    return parseBooleanValue(fieldValue);
  }
  if (FieldValueTypesByFieldType[FieldType.NUMERIC].includes(fieldType)) {
    // the source of the data could be an `<option>` value, which returns as a string
    // or an `input type="number"`, which returns a number.
    return parseInternalIndex(fieldValue);
  }
  return fieldValue;
}
